import React from "react";
import {
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CARE_PROFILE_SECTION_IDS } from "../../../utils/careProfileSections";
import colors from "../../../assets/theme/colors";
import { CustomizableAutocomplete } from "../../UI";
import SupportGoalsSection from "./SupportGoalsSection";
import { HOSPICE_PROFILE_SECTION_PROMPTS } from "./careProfileSectionPromptConfig";
import { getProfileLensOptions } from "./profileLensOptions";
import AllergiesFoodSection from "./AllergiesFoodSection";
import PersonIdentityFields from "./PersonIdentityFields";
import PersonAdministrativeFields from "./PersonAdministrativeFields";

const fieldSx = {
  "& .MuiAutocomplete-tag": {
    my: 0.25,
    maxWidth: "100%",
  },
  "& .MuiAutocomplete-inputRoot": {
    alignItems: "center",
    rowGap: 0.6,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: colors.semantic.profileFlow.fieldLabel,
  },
  "& .MuiInputBase-root": {
    minHeight: 54,
    borderRadius: "12px",
    bgcolor: colors.semantic.profileFlow.fieldBg,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.cardBorder,
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.focusBorder,
    borderWidth: "1px",
  },
};

const PromptCard = ({ title, children }) => (
  <Box
    sx={{
      p: { xs: 1.35, sm: 1.55 },
      borderRadius: 2.2,
      border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
      bgcolor: colors.semantic.profileFlow.cardBg,
    }}
  >
    <Stack spacing={1.05}>
      <Typography sx={{ fontSize: "0.96rem", fontWeight: 800, color: colors.landing.heroText }}>
        {title}
      </Typography>
      {children}
    </Stack>
  </Box>
);

const TagPrompt = ({ label, value, onChange, options, addText, helperText, renderTags = null }) => (
  <CustomizableAutocomplete
    options={options}
    value={value || []}
    onChange={(event, nextValue) => onChange(nextValue)}
    label={label}
    addText={addText}
    helperText={helperText}
    renderTags={renderTags}
    sx={fieldSx}
  />
);

const TextPrompt = ({ label, value, onChange, placeholder = "" }) => (
  <TextField
    label={label}
    placeholder={placeholder}
    multiline
    minRows={3}
    fullWidth
    value={value || ""}
    onChange={(event) => onChange(event.target.value)}
    sx={fieldSx}
  />
);

const CareProfileSectionEditor = ({
  sectionId,
  sectionLabel,
  displayName,
  name,
  setName,
  dateOfBirth,
  setDateOfBirth,
  pronouns,
  setPronouns,
  language,
  setLanguage,
  setPhoto,
  photoURL,
  setPhotoURL,
  selectedConditions,
  setSelectedConditions,
  careContext,
  setCareContext,
  child,
  foodAllergies,
  setFoodAllergies,
  dietaryRestrictions,
  setDietaryRestrictions,
  sensoryIssues,
  setSensoryIssues,
  behavioralTriggers,
  setBehavioralTriggers,
  communicationNeeds,
  setCommunicationNeeds,
  supportProfile,
  setSupportProfile,
  renderMedicationDetails,
  renderDocumentDropZone,
}) => {
  const safeSupportProfile = supportProfile || {};
  const whoTheyAre = safeSupportProfile.whoTheyAre || {};
  const titleName = String(displayName || name || "this person").trim();
  const isHospiceWhoToCallSection = sectionLabel === "Who to Call";
  const lensOptions = getProfileLensOptions(selectedConditions, careContext);
  const supportOptions = lensOptions.supportProfile;

  const updateSupportField = (key, value) => {
    setSupportProfile?.((current) => ({ ...(current || {}), [key]: value }));
  };
  const updateWhoTheyAre = (key, value) => {
    setSupportProfile?.((current) => ({
      ...(current || {}),
      whoTheyAre: { ...((current || {}).whoTheyAre || {}), [key]: value },
    }));
  };

  const renderConfiguredPromptField = (field) => {
    if (field.type === "supportText") {
      return (
        <TextPrompt
          key={`${field.type}-${field.key}`}
          label={field.label}
          value={safeSupportProfile[field.key]}
          onChange={(value) => updateSupportField(field.key, value)}
          placeholder={field.placeholder}
        />
      );
    }

    if (field.type === "supportTag") {
      return (
        <TagPrompt
          key={`${field.type}-${field.key}`}
          label={field.label}
          value={safeSupportProfile[field.key]}
          onChange={(value) => updateSupportField(field.key, value)}
          options={supportOptions[field.optionsKey] || []}
          addText={field.addText}
        />
      );
    }

    if (field.type === "communicationNeeds") {
      return (
        <TagPrompt
          key={field.type}
          label={field.label}
          value={communicationNeeds}
          onChange={setCommunicationNeeds}
          options={lensOptions.communication}
          addText={field.addText}
        />
      );
    }

    return null;
  };

  const renderConfiguredPrompts = (prompts = []) => prompts.map((prompt) => (
    <PromptCard key={prompt.title} title={prompt.title}>
      {prompt.fields.map(renderConfiguredPromptField)}
    </PromptCard>
  ));

  const behaviorPrompts = (
    <>
      <PromptCard title="What tends to trigger hard moments?">
        <TagPrompt label="Triggers" value={behavioralTriggers} onChange={setBehavioralTriggers} options={lensOptions.triggers} addText="Add trigger" />
      </PromptCard>
      <PromptCard title="What are the early warning signs?">
        <TagPrompt label="Early warning signs" value={safeSupportProfile.earlyWarningSigns} onChange={(value) => updateSupportField("earlyWarningSigns", value)} options={supportOptions.earlyWarningSigns} addText="Add sign" />
      </PromptCard>
      <PromptCard title="What helps in the moment?">
        <TagPrompt label="What helps" value={safeSupportProfile.thingsThatUsuallyHelp} onChange={(value) => updateSupportField("thingsThatUsuallyHelp", value)} options={supportOptions.thingsThatUsuallyHelp} addText="Add help" />
      </PromptCard>
      <PromptCard title="What safety notes should caregivers know?">
        <TagPrompt label="Safety notes" value={safeSupportProfile.safetyConsiderations} onChange={(value) => updateSupportField("safetyConsiderations", value)} options={supportOptions.safetyConsiderations} addText="Add safety note" />
      </PromptCard>
    </>
  );

  const supportPrompts = (
    <>
      <PromptCard title="How does this person communicate best?">
        <TagPrompt label="Communication style" value={safeSupportProfile.communicationStyle} onChange={(value) => updateSupportField("communicationStyle", value)} options={supportOptions.communicationStyle} addText="Add style" />
        <TagPrompt label="Communication support" value={communicationNeeds} onChange={setCommunicationNeeds} options={lensOptions.communication} addText="Add support" />
      </PromptCard>
      <PromptCard title="What helps when they are tired or overwhelmed?">
        <TagPrompt label="Helpful supports" value={safeSupportProfile.whatHelps} onChange={(value) => updateSupportField("whatHelps", value)} options={supportOptions.whatHelps} addText="Add support" />
      </PromptCard>
      <PromptCard title="What should caregivers avoid saying or doing?">
        <TagPrompt label="Things to avoid" value={safeSupportProfile.thingsToAvoid} onChange={(value) => updateSupportField("thingsToAvoid", value)} options={supportOptions.thingsToAvoid} addText="Add avoid" />
      </PromptCard>
      <PromptCard title="What environment works best?">
        <TextPrompt label="Environment and support notes" value={safeSupportProfile.communicationNotes} onChange={(value) => updateSupportField("communicationNotes", value)} placeholder="Quiet room, low voice, fewer people, extra time..." />
      </PromptCard>
    </>
  );

  const sectionContent = {
    [CARE_PROFILE_SECTION_IDS.ABOUT]: (
      <>
        <PromptCard title={`Who is ${titleName}?`}>
          <PersonIdentityFields
            name={name}
            setName={setName}
            dateOfBirth={dateOfBirth}
            onDateOfBirthChange={setDateOfBirth}
            pronouns={pronouns}
            setPronouns={setPronouns}
            language={language}
            setLanguage={setLanguage}
            setPhoto={setPhoto}
            photoURL={photoURL}
            setPhotoURL={setPhotoURL}
            selectedConditions={selectedConditions}
            setSelectedConditions={setSelectedConditions}
            fieldSx={fieldSx}
          />
        </PromptCard>
        <PromptCard title="Living arrangement and emergency contacts">
          <PersonAdministrativeFields
            careContext={careContext}
            onCareSettingChange={(careSetting) => setCareContext?.((current) => ({
              ...(current || {}),
              careSetting,
            }))}
            emergencyContacts={safeSupportProfile.emergencyContacts}
            onEmergencyContactsChange={(value) => updateSupportField("emergencyContacts", value)}
            child={child}
            suggestedCareSettings={lensOptions.careSetting}
            fieldSx={fieldSx}
          />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.MEDICATION]: (
      <PromptCard title="What medications should the care circle know about?">
        {renderMedicationDetails?.()}
      </PromptCard>
    ),
    [CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD]: (
      <>
        <AllergiesFoodSection
          childName={titleName}
          foodAllergies={foodAllergies}
          setFoodAllergies={setFoodAllergies}
          dietaryRestrictions={dietaryRestrictions}
          setDietaryRestrictions={setDietaryRestrictions}
          renderDocumentDropZone={renderDocumentDropZone}
        />
        <PromptCard title="What foods are usually safe or preferred?">
          <TagPrompt label="Favorite or preferred foods" value={whoTheyAre.favoriteFoods} onChange={(value) => updateWhoTheyAre("favoriteFoods", value)} options={supportOptions.favoriteFoods} addText="Add food" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT]: supportPrompts,
    [CARE_PROFILE_SECTION_IDS.GOALS]: (
      <SupportGoalsSection supportProfile={safeSupportProfile} setSupportProfile={setSupportProfile} childDisplayName={titleName} sectionGroup="goals" showIntro={false} supportOptions={supportOptions} />
    ),
    [CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT]: behaviorPrompts,
    [CARE_PROFILE_SECTION_IDS.SENSORY_PROFILE]: (
      <>
        <PromptCard title="What sensory input is hard?">
          <TagPrompt label="Sensory or comfort needs" value={sensoryIssues} onChange={setSensoryIssues} options={lensOptions.sensory} addText="Add need" />
        </PromptCard>
        <PromptCard title="What sensory tools or supports help?">
          <TagPrompt label="Helpful tools" value={safeSupportProfile.thingsThatUsuallyHelp} onChange={(value) => updateSupportField("thingsThatUsuallyHelp", value)} options={lensOptions.sensorySupports} addText="Add tool" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.ROUTINE_TRANSITIONS]: (
      <>
        <PromptCard title="What routines or transitions are hard?">
          <TagPrompt label="Hard moments" value={safeSupportProfile.whatCanBeDifficult} onChange={(value) => updateSupportField("whatCanBeDifficult", value)} options={lensOptions.transitionChallenges} addText="Add hard thing" />
        </PromptCard>
        <PromptCard title="What helps transitions go better?">
          <TagPrompt label="Transition supports" value={safeSupportProfile.thingsThatUsuallyHelp} onChange={(value) => updateSupportField("thingsThatUsuallyHelp", value)} options={lensOptions.transitionSupports} addText="Add support" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.COMMUNICATION]: supportPrompts,
    [CARE_PROFILE_SECTION_IDS.MEMORY_ORIENTATION]: (
      <>
        <PromptCard title="What is still familiar or grounding?">
          <TextPrompt label="Memory and orientation notes" value={safeSupportProfile.about} onChange={(value) => updateSupportField("about", value)} placeholder="People, places, time periods, routines, or anchors that still feel familiar." />
        </PromptCard>
        <PromptCard title="What causes confusion or distress?">
          <TagPrompt label="Confusing or difficult moments" value={safeSupportProfile.whatCanBeDifficult} onChange={(value) => updateSupportField("whatCanBeDifficult", value)} options={lensOptions.memoryChallenges} addText="Add difficulty" />
        </PromptCard>
        <PromptCard title="What orientation anchors help?">
          <TagPrompt label="Helpful anchors" value={safeSupportProfile.whatHelps} onChange={(value) => updateSupportField("whatHelps", value)} options={lensOptions.memoryAnchors} addText="Add anchor" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.MOBILITY_SAFETY]: (
      <>
        <PromptCard title="What mobility or safety risks matter?">
          <TagPrompt label="Safety notes" value={safeSupportProfile.safetyConsiderations} onChange={(value) => updateSupportField("safetyConsiderations", value)} options={supportOptions.safetyConsiderations} addText="Add safety note" />
        </PromptCard>
        <PromptCard title="What support helps on harder days?">
          <TagPrompt label="Helpful supports" value={safeSupportProfile.thingsThatUsuallyHelp} onChange={(value) => updateSupportField("thingsThatUsuallyHelp", value)} options={lensOptions.mobilitySupports} addText="Add support" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.SLEEP]: (
      <>
        <PromptCard title="What sleep or rest patterns should caregivers know?">
          <TagPrompt label="Sleep, rest, or fatigue notes" value={safeSupportProfile.whatCanBeDifficult} onChange={(value) => updateSupportField("whatCanBeDifficult", value)} options={lensOptions.sleepRestPatterns} addText="Add note" />
        </PromptCard>
        <PromptCard title="What helps with rest or overnight safety?">
          <TagPrompt label="Rest supports" value={safeSupportProfile.whatHelps} onChange={(value) => updateSupportField("whatHelps", value)} options={lensOptions.sleepRestSupports} addText="Add support" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.SYMPTOMS_MONITORING]: (
      <>
        <PromptCard title="What symptoms should caregivers monitor?">
          <TagPrompt label="Warning signs" value={safeSupportProfile.earlyWarningSigns} onChange={(value) => updateSupportField("earlyWarningSigns", value)} options={supportOptions.earlyWarningSigns} addText="Add sign" />
        </PromptCard>
        <PromptCard title="What makes symptoms worse or better?">
          <TagPrompt label="Patterns and difficult moments" value={safeSupportProfile.whatCanBeDifficult} onChange={(value) => updateSupportField("whatCanBeDifficult", value)} options={lensOptions.symptomPatterns} addText="Add pattern" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.COMFORT_CARE]: renderConfiguredPrompts(HOSPICE_PROFILE_SECTION_PROMPTS[CARE_PROFILE_SECTION_IDS.COMFORT_CARE]),
    [CARE_PROFILE_SECTION_IDS.CARE_WISHES]: renderConfiguredPrompts(HOSPICE_PROFILE_SECTION_PROMPTS[CARE_PROFILE_SECTION_IDS.CARE_WISHES]),
    [CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL]: isHospiceWhoToCallSection
      ? renderConfiguredPrompts(HOSPICE_PROFILE_SECTION_PROMPTS[CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL])
      : (
      <>
        <PromptCard title="What should caregivers do in an urgent situation?">
          <TagPrompt label="Emergency and safety notes" value={safeSupportProfile.safetyConsiderations} onChange={(value) => updateSupportField("safetyConsiderations", value)} options={supportOptions.safetyConsiderations} addText="Add emergency note" />
        </PromptCard>
      </>
    ),
    [CARE_PROFILE_SECTION_IDS.DAILY_COMFORT_ROUTINE]: renderConfiguredPrompts(HOSPICE_PROFILE_SECTION_PROMPTS[CARE_PROFILE_SECTION_IDS.DAILY_COMFORT_ROUTINE]),
  };

  return (
    <Stack spacing={1.2}>
      <Box>
        <Typography sx={{ fontSize: "1.35rem", fontWeight: 850, color: colors.landing.heroText }}>
          {sectionLabel}
        </Typography>
      </Box>
      {sectionContent[sectionId] || supportPrompts}
    </Stack>
  );
};

export default CareProfileSectionEditor;
